import React, { useState, useMemo } from 'react'
import { Card, CardBody, Row, Col, Badge, Input, Label, Button, Spinner } from 'reactstrap'
import PageWrapper from '../../../components/Common/PageWrapper'
import TableContainer from '../../../components/Common/TableContainer'
import { useAuditLogs, useAuditLog } from '../../../hooks/useAuditLog'
import { useListParams } from '../../../hooks/useListParams'
import { formatReadableDate } from '../../../utils/formatDate'
import AuditLogView from './AuditLogView'

const AuditLog = () => {
  document.title = 'Audit Log - Aurobil'

  const [filters, setFilters] = useState({
    model_type: '',
    action: '',
    user_id: '',
  })

  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)

  const { data: detailData, isLoading: isDetailLoading } = useAuditLog(selectedViewId, !!selectedViewId)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({
    page: 1,
    search: '',
  })

  const queryParams = useMemo(
    () => ({
      page: params.page || 1,
      per_page: params.per_page || 50,
      search: params.search,
      ...filters,
    }),
    [params, filters]
  )

  const { data, isLoading, isRefetching, refetch } = useAuditLogs(queryParams)
  const paginationInfo = data?.data || {}
  const logs = data?.data?.data || []

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1) // Reset to page 1 when filter changes
  }

  const handleFilterReset = () => {
    setFilters({ model_type: '', action: '', user_id: '' })
    resetParams()
  }

  const columns = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        cell: ({ row }) => <span className="font-monospace fw-semibold">#{row.original.id}</span>,
      },
      {
        header: 'Entity',
        cell: ({ row }) => {
          const modelName = row.original.model_type?.split('\\').pop() || 'Unknown'
          return (
            <div>
              <span className="fw-semibold">{modelName}</span>
              <div className="small text-muted">ID: {row.original.model_id}</div>
            </div>
          )
        },
      },
      {
        header: 'Action',
        cell: ({ row }) => {
          const action = row.original.action
          const config = {
            create: { color: 'success', icon: 'mdi-plus-circle' },
            update: { color: 'info', icon: 'mdi-pencil-circle' },
            delete: { color: 'danger', icon: 'mdi-delete-circle' },
            restore: { color: 'warning', icon: 'mdi-restore' },
          }
          const c = config[action] || { color: 'secondary', icon: 'mdi-help-circle' }
          return (
            <Badge color={c.color} className="d-flex align-items-center gap-1 w-fit">
              <i className={`mdi ${c.icon}`} />
              <span className="text-capitalize">{action}</span>
            </Badge>
          )
        },
      },
      {
        header: 'User',
        cell: ({ row }) => (
          <div className="small">
            {row.original.user?.name || row.original.user?.username || (
              <span className="text-muted italic">System</span>
            )}
          </div>
        ),
      },
      {
        header: 'IP Address',
        cell: ({ row }) => (
          <code className="small text-muted">{row.original.ip_address || 'N/A'}</code>
        ),
      },
      {
        header: 'Timestamp',
        cell: ({ row }) => (
          <small className="text-muted">{formatReadableDate(row.original.created_at)}</small>
        ),
      },
      {
        header: 'Changes',
        cell: ({ row }) => {
          const changeCount =
            (Object.keys(row.original.old_values || {}).length || 0) +
            (Object.keys(row.original.new_values || {}).length || 0)
          return (
            <Badge color="light" className="text-dark small">
              <i className="mdi mdi-file-document-outline me-1" />
              {changeCount} change{changeCount !== 1 ? 's' : ''}
            </Badge>
          )
        },
      },
      {
        header: 'Action',
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button
                color="primary"
                size="sm"
                onClick={() => {
                  setSelectedViewId(row.original.id)
                  setViewModal(true)
                }}
                id={`view-audit-${row.original.id}`}
                title="View full details"
              >
                <i className="mdi mdi-eye-outline" />
              </Button>
            </li>
          </ul>
        ),
      },
    ],
    []
  )

  const MODEL_TYPES = [
    { value: 'App\\Models\\Account', label: 'Account' },
    { value: 'App\\Models\\AccountExpense', label: 'Account Expense' },
    { value: 'App\\Models\\AccountLoan', label: 'Account Loan' },
    { value: 'App\\Models\\AccountVendorBill', label: 'Vendor Bill' },
    { value: 'App\\Models\\AccountCustomerPayment', label: 'Customer Payment' },
    { value: 'App\\Models\\AccountFundTransfer', label: 'Fund Transfer' },
    { value: 'App\\Models\\AccountImporterPayment', label: 'Importer Payment' },
  ]

  const ACTIONS = [
    { value: 'create', label: 'Created' },
    { value: 'update', label: 'Updated' },
    { value: 'delete', label: 'Deleted' },
    { value: 'restore', label: 'Restored' },
  ]

  return (
    <PageWrapper title="Audit Log" breadcrumbItem="Audit Log">
      <Card>
        <CardBody>
          {/* Header Info Card */}
          <div className="mb-4 p-3 bg-light border rounded">
            <h6 className="mb-2 fw-semibold">
              <i className="mdi mdi-history me-2 text-primary" />
              Change History Tracking
            </h6>
            <p className="small text-muted mb-0">
              Complete audit trail of all changes to account entities. Filter by entity type, action, or user to
              track specific changes.
            </p>
          </div>

          {/* Filters */}
          <Row className="g-2 align-items-end mb-3">
            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">Entity Type</Label>
              <Input
                type="select"
                bsSize="sm"
                value={filters.model_type || ''}
                onChange={(e) => handleFilterChange('model_type', e.target.value)}
              >
                <option value="">All Types</option>
                {MODEL_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Input>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">Action</Label>
              <Input
                type="select"
                bsSize="sm"
                value={filters.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <option value="">All Actions</option>
                {ACTIONS.map((action) => (
                  <option key={action.value} value={action.value}>
                    {action.label}
                  </option>
                ))}
              </Input>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">Entity ID</Label>
              <Input
                type="text"
                bsSize="sm"
                placeholder="Search by ID"
                value={filters.user_id || ''}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
              />
            </Col>

            <Col xs="auto">
              <Button
                color="secondary"
                size="sm"
                outline
                onClick={handleFilterReset}
                disabled={!hasActiveFilters && Object.values(filters).every((v) => !v)}
              >
                <i className="mdi mdi-refresh me-1" />
                Reset
              </Button>
            </Col>
          </Row>

          {/* Table */}
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner size="sm" color="primary" />
              <p className="text-muted small mt-2">Loading audit logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="mdi mdi-inbox-outline display-4 opacity-50" />
              <p className="mt-2">No audit logs found</p>
            </div>
          ) : (
            <>
              <TableContainer
                columns={columns}
                data={logs}
                isPagination={!!paginationInfo}
                paginationInfo={paginationInfo}
                onPageChange={setPage}
                loading={isLoading || isRefetching}
              />

              {/* Info Footer */}
              <div className="small text-muted mt-3 d-flex justify-content-between align-items-center">
                <span>
                  Showing {logs.length} of {paginationInfo.total} audit logs
                </span>
                <Button
                  color="primary"
                  size="sm"
                  outline
                  onClick={() => refetch()}
                  disabled={isRefetching}
                >
                  <i className="mdi mdi-refresh me-1" />
                  Refresh
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Audit Log View Modal */}
      <AuditLogView
        isOpen={viewModal}
        toggle={() => { setViewModal(false); setSelectedViewId(null) }}
        auditLog={detailData?.data}
        isLoading={isDetailLoading}
      />
    </PageWrapper>
  )
}

export default AuditLog
